import { FilterLinesIcon, PlusIcon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { Button, IconButton } from '@/design-system';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { Popover, PopoverContent, PopoverTrigger } from '../../Popover';
import {
  IncompleteSessionFilter,
  SessionFilter,
} from '../types/SessionsFilterModel';
import { SessionFilterBarOperands } from './SessionsFilterBarOperands';

// Remove this for the time being since filtering doesn't work on the backend (because it's an array)
const EXCLUDED_METADATA = ['dataset_splits'];

const pluralRules = new Intl.PluralRules('en-US');

function SessionFilterDialogContent(props: {
  commonMetadata: Record<string, string[]>;
  commonFeedback: string[];
  filters: IncompleteSessionFilter[];
  activeFilters: SessionFilter[];
  onChange: (value: IncompleteSessionFilter[]) => void;
}) {
  const commonMetadata = Object.fromEntries(
    Object.entries(props.commonMetadata).filter(
      ([k, _]) => !EXCLUDED_METADATA.includes(k)
    )
  );

  return (
    <div className="group flex flex-col items-stretch border-tertiary p-3">
      <div className="flex flex-col pb-1">
        <div className="flex items-start justify-between">
          <span className="text-sm font-medium">Filters</span>

          <div className="flex items-start gap-1 opacity-0 transition-all focus-within:opacity-100 group-hover:opacity-100 [&:has([data-state=open])]:opacity-100">
            {props.filters.length && (
              <IconButton
                icon={TrashIcon}
                label="Clear all filters"
                onClick={() => props.onChange([])}
                variant="outlined"
                color="secondary"
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <SessionFilterBarOperands
          currentFilters={props.filters}
          commonFeedback={props.commonFeedback}
          commonMetadata={commonMetadata}
          onChange={props.onChange}
        />
        <Button
          onClick={() => {
            props.onChange([...props.filters, {}]);
          }}
          leftDecorator={PlusIcon}
          variant="outlined"
          color="secondary"
          className="self-start"
        >
          Add filter
        </Button>
      </div>
    </div>
  );
}

export function SessionsFilterBar(props: {
  commonMetadata: Record<string, string[]>;
  commonFeedback: string[];
  filters: IncompleteSessionFilter[];
  activeFilters: SessionFilter[];
  onChange: (value: IncompleteSessionFilter[]) => void;
}) {
  const nActive = props.activeFilters.length;
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="my-auto flex h-fit gap-1.5 rounded-md border border-secondary px-2 py-1 text-xs font-medium transition-all hover:bg-secondary active:bg-secondary data-[state=open]:bg-tertiary"
        >
          <FilterLinesIcon className="h-4 w-4" />
          {nActive === 0 ? (
            <span>Filters</span>
          ) : (
            <span>
              {
                {
                  one: `1 filter`,
                  two: `${nActive} filters`,
                  few: `${nActive} filters`,
                  other: `${nActive} filters`,
                }[pluralRules.select(nActive)]
              }
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className={cn('z-10 ml-10 w-auto min-w-[300px] overflow-hidden p-0')}
        side="bottom"
        avoidCollisions
      >
        <SessionFilterDialogContent
          commonMetadata={props.commonMetadata}
          commonFeedback={props.commonFeedback}
          filters={props.filters}
          activeFilters={props.activeFilters}
          onChange={props.onChange}
        />
      </PopoverContent>
    </Popover>
  );
}
